/* eslint-disable no-undef */
/* eslint-disable no-unused-vars */
import AsyncStorage from '@react-native-async-storage/async-storage';

/**
 * Psychometric Test Service
 * Handles personality assessments, cognitive tests, and behavioral analysis
 */
class PsychometricTestService {
  constructor() {
    this.isInitialized = false;
    this.currentTest = null;
    this.testBank = null;
    this.analysisEngine = null;
  }

  /**
   * Safe property access helper
   */
  safeGet(obj, path, defaultValue = null) {
    try {
      if (!obj || typeof obj !== 'object') {
        return defaultValue;
      }
      
      const keys = path.split('.');
      let result = obj;
      for (const key of keys) {
        if (result === null || result === undefined || typeof result !== 'object') {
          return defaultValue;
        }
        result = result[key];
      }
      return result !== undefined ? result : defaultValue;
    } catch (error) {
      console.error('Error in safeGet:', error);
      return defaultValue;
    }
  }

  /**
   * Initialize Psychometric Test Service
   */
  async initialize() {
    try {
      console.log('Initializing PsychometricTestService...');
      
      // Load test bank
      await this.loadTestBank();
      
      // Initialize analysis engine
      await this.initializeAnalysisEngine();
      
      // Load test settings
      await this.loadTestSettings();
      
      this.isInitialized = true;
      console.log('✅ PsychometricTestService initialized successfully');
      
      return {
        success: true,
        message: 'Psychometric test service initialized',
        testTypes: ['personality', 'cognitive', 'behavioral', 'emotional_intelligence'],
        timestamp: new Date().toISOString()
      };
      
    } catch (error) {
      console.error('❌ Error initializing PsychometricTestService:', error);
      
      // Fallback initialization
      this.isInitialized = true; // Allow app to continue
      
      return {
        success: false,
        error: error.message,
        fallback: true
      };
    }
  }

  /**
   * Load test bank
   */
  async loadTestBank() {
    try {
      // Load test questions from storage or use defaults
      const testData = await AsyncStorage.getItem('psychometric_test_bank');
      
      this.testBank = testData ? JSON.parse(testData) : this.getDefaultTestBank();
      
      console.log('✅ Test bank loaded with', Object.keys(this.testBank).length, 'test types');
      
    } catch (error) {
      console.error('❌ Error loading test bank:', error);
      this.testBank = this.getDefaultTestBank();
    }
  }

  /**
   * Initialize analysis engine
   */
  async initializeAnalysisEngine() {
    try {
      // Mock analysis engine initialization
      this.analysisEngine = {
        personality: {
          bigFive: true,
          mbti: false,
          disc: false
        },
        cognitive: {
          iq: true,
          reasoning: true,
          memory: true,
          processing: true
        },
        behavioral: {
          leadership: true,
          teamwork: true,
          communication: true,
          problemSolving: true
        },
        emotional: {
          selfAwareness: true,
          empathy: true,
          socialSkills: true,
          motivation: true
        }
      };
      
      console.log('✅ Analysis engine initialized');
      
    } catch (error) {
      console.error('❌ Error initializing analysis engine:', error);
      this.analysisEngine = { basic: true };
    }
  }

  /**
   * Load test settings
   */
  async loadTestSettings() {
    try {
      const settingsData = await AsyncStorage.getItem('psychometric_test_settings');
      
      this.testSettings = settingsData ? JSON.parse(settingsData) : {
        timeLimit: {
          personality: 1200, // 20 minutes
          cognitive: 1800, // 30 minutes
          behavioral: 900, // 15 minutes
          emotional_intelligence: 1200 // 20 minutes
        },
        questionCount: {
          personality: 50,
          cognitive: 30,
          behavioral: 25,
          emotional_intelligence: 40
        },
        adaptiveTesting: true,
        randomizeQuestions: true,
        showProgress: true,
        allowReview: false
      };
      
      console.log('✅ Test settings loaded');
      
    } catch (error) {
      console.error('❌ Error loading test settings:', error);
      this.testSettings = { timeLimit: { default: 1200 } };
    }
  }

  /**
   * Start psychometric test
   */
  async startPsychometricTest(testType, candidateData, jobData) {
    try {
      console.log('Starting psychometric test:', testType, 'for candidate:', this.safeGet(candidateData, 'candidateId', 'unknown'));
      
      // Validate test type
      const validTypes = ['personality', 'cognitive', 'behavioral', 'emotional_intelligence'];
      if (!validTypes.includes(testType)) {
        throw new Error(`Invalid test type: ${testType}`);
      }
      
      // Generate test questions
      const questions = await this.generateTestQuestions(testType, candidateData, jobData);
      
      // Create test session
      const testSession = {
        testId: `psychometric_${testType}_${Date.now()}`,
        testType: testType,
        candidateId: this.safeGet(candidateData, 'candidateId', 'unknown'),
        candidateData: candidateData || {},
        jobData: jobData || {},
        questions: questions,
        currentQuestionIndex: 0,
        responses: [],
        startTime: new Date().toISOString(),
        status: 'in_progress',
        timeLimit: this.testSettings.timeLimit[testType] || 1200,
        totalQuestions: questions.length,
        analytics: {
          timeSpent: 0,
          questionsAnswered: 0,
          averageResponseTime: 0,
          confidenceLevel: 0
        }
      };
      
      // Store current test
      this.currentTest = testSession;
      
      console.log('✅ Psychometric test started successfully');
      
      return {
        success: true,
        testId: testSession.testId,
        testType: testType,
        totalQuestions: questions.length,
        timeLimit: testSession.timeLimit,
        firstQuestion: questions[0],
        message: 'Psychometric test started successfully'
      };
      
    } catch (error) {
      console.error('❌ Error starting psychometric test:', error);
      
      // Return fallback test
      return this.getFallbackTest(testType, candidateData);
    }
  }

  /**
   * Generate test questions based on type and context
   */
  async generateTestQuestions(testType, candidateData, jobData) {
    try {
      const jobTitle = this.safeGet(jobData, 'title', 'Software Developer');
      const jobLevel = this.safeGet(jobData, 'level', 'mid');
      const requiredSkills = this.safeGet(jobData, 'skills', []);
      
      let questions = [];
      
      switch (testType) {
        case 'personality':
          questions = this.generatePersonalityQuestions(jobTitle, jobLevel);
          break;
        case 'cognitive':
          questions = this.generateCognitiveQuestions(jobTitle, jobLevel);
          break;
        case 'behavioral':
          questions = this.generateBehavioralQuestions(jobTitle, jobLevel);
          break;
        case 'emotional_intelligence':
          questions = this.generateEmotionalIntelligenceQuestions(jobTitle, jobLevel);
          break;
        default:
          questions = this.getDefaultQuestions(testType);
      }
      
      // Randomize if enabled
      if (this.testSettings.randomizeQuestions) {
        questions = this.shuffleArray(questions);
      }
      
      // Limit to configured count
      const maxQuestions = this.testSettings.questionCount[testType] || 25;
      questions = questions.slice(0, maxQuestions);
      
      console.log(`Generated ${questions.length} ${testType} questions`);
      return questions;
      
    } catch (error) {
      console.error('Error generating test questions:', error);
      return this.getDefaultQuestions(testType);
    }
  }

  /**
   * Generate personality questions (Big Five model)
   */
  generatePersonalityQuestions(jobTitle, jobLevel) {
    const personalityQuestions = [
      // Openness to Experience
      {
        id: 'personality_1',
        category: 'openness',
        question: 'I enjoy exploring new ideas and concepts.',
        type: 'likert',
        scale: 5,
        options: ['Strongly Disagree', 'Disagree', 'Neutral', 'Agree', 'Strongly Agree'],
        trait: 'openness',
        reverse: false
      },
      {
        id: 'personality_2',
        category: 'openness',
        question: 'I prefer routine and familiar ways of doing things.',
        type: 'likert',
        scale: 5,
        options: ['Strongly Disagree', 'Disagree', 'Neutral', 'Agree', 'Strongly Agree'],
        trait: 'openness',
        reverse: true
      },
      {
        id: 'personality_3',
        category: 'openness',
        question: 'I am interested in learning about different cultures and perspectives.',
        type: 'likert',
        scale: 5,
        options: ['Strongly Disagree', 'Disagree', 'Neutral', 'Agree', 'Strongly Agree'],
        trait: 'openness',
        reverse: false
      },
      
      // Conscientiousness
      {
        id: 'personality_4',
        category: 'conscientiousness',
        question: 'I am always prepared and organized.',
        type: 'likert',
        scale: 5,
        options: ['Strongly Disagree', 'Disagree', 'Neutral', 'Agree', 'Strongly Agree'],
        trait: 'conscientiousness',
        reverse: false
      },
      {
        id: 'personality_5',
        category: 'conscientiousness',
        question: 'I often leave tasks until the last minute.',
        type: 'likert',
        scale: 5,
        options: ['Strongly Disagree', 'Disagree', 'Neutral', 'Agree', 'Strongly Agree'],
        trait: 'conscientiousness',
        reverse: true
      },
      {
        id: 'personality_6',
        category: 'conscientiousness',
        question: 'I pay attention to details and strive for accuracy.',
        type: 'likert',
        scale: 5,
        options: ['Strongly Disagree', 'Disagree', 'Neutral', 'Agree', 'Strongly Agree'],
        trait: 'conscientiousness',
        reverse: false
      },
      
      // Extraversion
      {
        id: 'personality_7',
        category: 'extraversion',
        question: 'I feel energized when I am around other people.',
        type: 'likert',
        scale: 5,
        options: ['Strongly Disagree', 'Disagree', 'Neutral', 'Agree', 'Strongly Agree'],
        trait: 'extraversion',
        reverse: false
      },
      {
        id: 'personality_8',
        category: 'extraversion',
        question: 'I prefer working alone rather than in groups.',
        type: 'likert',
        scale: 5,
        options: ['Strongly Disagree', 'Disagree', 'Neutral', 'Agree', 'Strongly Agree'],
        trait: 'extraversion',
        reverse: true
      },
      {
        id: 'personality_9',
        category: 'extraversion',
        question: 'I enjoy being the center of attention.',
        type: 'likert',
        scale: 5,
        options: ['Strongly Disagree', 'Disagree', 'Neutral', 'Agree', 'Strongly Agree'],
        trait: 'extraversion',
        reverse: false
      },
      
      // Agreeableness
      {
        id: 'personality_10',
        category: 'agreeableness',
        question: 'I am generally trusting of others.',
        type: 'likert',
        scale: 5,
        options: ['Strongly Disagree', 'Disagree', 'Neutral', 'Agree', 'Strongly Agree'],
        trait: 'agreeableness',
        reverse: false
      },
      {
        id: 'personality_11',
        category: 'agreeableness',
        question: 'I tend to be critical of others.',
        type: 'likert',
        scale: 5,
        options: ['Strongly Disagree', 'Disagree', 'Neutral', 'Agree', 'Strongly Agree'],
        trait: 'agreeableness',
        reverse: true
      },
      {
        id: 'personality_12',
        category: 'agreeableness',
        question: 'I try to be helpful and considerate to others.',
        type: 'likert',
        scale: 5,
        options: ['Strongly Disagree', 'Disagree', 'Neutral', 'Agree', 'Strongly Agree'],
        trait: 'agreeableness',
        reverse: false
      },
      
      // Neuroticism
      {
        id: 'personality_13',
        category: 'neuroticism',
        question: 'I often feel anxious or worried.',
        type: 'likert',
        scale: 5,
        options: ['Strongly Disagree', 'Disagree', 'Neutral', 'Agree', 'Strongly Agree'],
        trait: 'neuroticism',
        reverse: false
      },
      {
        id: 'personality_14',
        category: 'neuroticism',
        question: 'I remain calm under pressure.',
        type: 'likert',
        scale: 5,
        options: ['Strongly Disagree', 'Disagree', 'Neutral', 'Agree', 'Strongly Agree'],
        trait: 'neuroticism',
        reverse: true
      },
      {
        id: 'personality_15',
        category: 'neuroticism',
        question: 'I get upset easily.',
        type: 'likert',
        scale: 5,
        options: ['Strongly Disagree', 'Disagree', 'Neutral', 'Agree', 'Strongly Agree'],
        trait: 'neuroticism',
        reverse: false
      }
    ];
    
    return personalityQuestions;
  }

  /**
   * Generate cognitive ability questions
   */
  generateCognitiveQuestions(jobTitle, jobLevel) {
    const cognitiveQuestions = [
      // Logical Reasoning
      {
        id: 'cognitive_1',
        category: 'logical_reasoning',
        question: 'If all roses are flowers and some flowers are red, which statement is necessarily true?',
        type: 'multiple_choice',
        options: [
          'All roses are red',
          'Some roses are red',
          'Some roses may be red',
          'No roses are red'
        ],
        correctAnswer: 2,
        explanation: 'We cannot determine if roses are red from the given information.',
        timeLimit: 120
      },
      {
        id: 'cognitive_2',
        category: 'logical_reasoning',
        question: 'Complete the sequence: 2, 6, 12, 20, 30, ?',
        type: 'multiple_choice',
        options: ['40', '42', '44', '46'],
        correctAnswer: 1,
        explanation: 'The differences are 4, 6, 8, 10, so next is 12: 30 + 12 = 42',
        timeLimit: 120
      },
      
      // Numerical Reasoning
      {
        id: 'cognitive_3',
        category: 'numerical_reasoning',
        question: 'A product costs £80 after a 20% discount. What was the original price?',
        type: 'multiple_choice',
        options: ['£96', '£100', '£104', '£120'],
        correctAnswer: 1,
        explanation: 'If £80 is 80% of original price, then original = £80 ÷ 0.8 = £100',
        timeLimit: 180
      },
      {
        id: 'cognitive_4',
        category: 'numerical_reasoning',
        question: 'If 3x + 7 = 22, what is the value of x?',
        type: 'multiple_choice',
        options: ['3', '5', '7', '9'],
        correctAnswer: 1,
        explanation: '3x = 22 - 7 = 15, so x = 15 ÷ 3 = 5',
        timeLimit: 120
      },
      
      // Spatial Reasoning
      {
        id: 'cognitive_5',
        category: 'spatial_reasoning',
        question: 'Which shape would result from folding the given pattern?',
        type: 'multiple_choice',
        options: ['Cube', 'Pyramid', 'Cylinder', 'Cone'],
        correctAnswer: 0,
        explanation: 'The pattern shows 6 connected squares forming a cube net.',
        timeLimit: 180
      },
      
      // Verbal Reasoning
      {
        id: 'cognitive_6',
        category: 'verbal_reasoning',
        question: 'Choose the word that best completes the analogy: Book is to Library as Car is to ?',
        type: 'multiple_choice',
        options: ['Road', 'Garage', 'Engine', 'Driver'],
        correctAnswer: 1,
        explanation: 'Books are stored in libraries, cars are stored in garages.',
        timeLimit: 120
      },
      
      // Working Memory
      {
        id: 'cognitive_7',
        category: 'working_memory',
        question: 'Remember this sequence: 7, 3, 9, 1, 5. Now, what is the sum of the 2nd and 4th numbers?',
        type: 'multiple_choice',
        options: ['4', '6', '8', '10'],
        correctAnswer: 0,
        explanation: '2nd number is 3, 4th number is 1, sum = 3 + 1 = 4',
        timeLimit: 90
      },
      
      // Processing Speed
      {
        id: 'cognitive_8',
        category: 'processing_speed',
        question: 'Count how many times the letter "A" appears in: ABACADAEAFAGA',
        type: 'multiple_choice',
        options: ['5', '6', '7', '8'],
        correctAnswer: 2,
        explanation: 'A appears at positions: 1, 3, 5, 7, 9, 11, 13 = 7 times',
        timeLimit: 60
      }
    ];
    
    return cognitiveQuestions;
  }

  /**
   * Generate behavioral questions
   */
  generateBehavioralQuestions(jobTitle, jobLevel) {
    const behavioralQuestions = [
      {
        id: 'behavioral_1',
        category: 'leadership',
        question: 'When leading a team project, I prefer to:',
        type: 'multiple_choice',
        options: [
          'Give detailed instructions and monitor progress closely',
          'Set clear goals and let team members work independently',
          'Collaborate with the team to develop the approach together',
          'Delegate tasks based on individual strengths'
        ],
        traits: ['leadership_style', 'delegation', 'team_management']
      },
      {
        id: 'behavioral_2',
        category: 'teamwork',
        question: 'In a team meeting where there is disagreement, I typically:',
        type: 'multiple_choice',
        options: [
          'Listen to all viewpoints before expressing my opinion',
          'Advocate strongly for my position',
          'Try to find a compromise that satisfies everyone',
          'Defer to the most senior team member'
        ],
        traits: ['conflict_resolution', 'communication', 'collaboration']
      },
      {
        id: 'behavioral_3',
        category: 'problem_solving',
        question: 'When faced with a complex problem, my first step is usually to:',
        type: 'multiple_choice',
        options: [
          'Break it down into smaller, manageable parts',
          'Research similar problems and solutions',
          'Brainstorm multiple approaches with others',
          'Start working on the most obvious solution'
        ],
        traits: ['analytical_thinking', 'problem_solving', 'methodology']
      },
      {
        id: 'behavioral_4',
        category: 'communication',
        question: 'When explaining technical concepts to non-technical stakeholders, I:',
        type: 'multiple_choice',
        options: [
          'Use analogies and simple language',
          'Provide detailed technical explanations',
          'Focus on the business impact and benefits',
          'Use visual aids and demonstrations'
        ],
        traits: ['communication', 'technical_translation', 'stakeholder_management']
      },
      {
        id: 'behavioral_5',
        category: 'adaptability',
        question: 'When project requirements change significantly, I:',
        type: 'multiple_choice',
        options: [
          'Assess the impact and adjust plans accordingly',
          'Express concerns about the changes',
          'Embrace the changes as new opportunities',
          'Seek clarification on the reasons for changes'
        ],
        traits: ['adaptability', 'change_management', 'flexibility']
      }
    ];
    
    return behavioralQuestions;
  }

  /**
   * Generate emotional intelligence questions
   */
  generateEmotionalIntelligenceQuestions(jobTitle, jobLevel) {
    const emotionalQuestions = [
      {
        id: 'emotional_1',
        category: 'self_awareness',
        question: 'I am usually aware of my emotions as they occur.',
        type: 'likert',
        scale: 5,
        options: ['Never', 'Rarely', 'Sometimes', 'Often', 'Always'],
        trait: 'self_awareness'
      },
      {
        id: 'emotional_2',
        category: 'self_regulation',
        question: 'When I am angry, I can control my emotions and respond appropriately.',
        type: 'likert',
        scale: 5,
        options: ['Never', 'Rarely', 'Sometimes', 'Often', 'Always'],
        trait: 'self_regulation'
      },
      {
        id: 'emotional_3',
        category: 'empathy',
        question: 'I can easily understand how others are feeling.',
        type: 'likert',
        scale: 5,
        options: ['Never', 'Rarely', 'Sometimes', 'Often', 'Always'],
        trait: 'empathy'
      },
      {
        id: 'emotional_4',
        category: 'social_skills',
        question: 'I am good at managing relationships and building rapport.',
        type: 'likert',
        scale: 5,
        options: ['Never', 'Rarely', 'Sometimes', 'Often', 'Always'],
        trait: 'social_skills'
      },
      {
        id: 'emotional_5',
        category: 'motivation',
        question: 'I am driven to achieve goals for personal satisfaction rather than external rewards.',
        type: 'likert',
        scale: 5,
        options: ['Never', 'Rarely', 'Sometimes', 'Often', 'Always'],
        trait: 'intrinsic_motivation'
      }
    ];
    
    return emotionalQuestions;
  }

  /**
   * Submit test response
   */
  async submitTestResponse(questionId, response, responseTime = 0) {
    try {
      if (!this.currentTest) {
        throw new Error('No active test session');
      }

      const currentQuestion = this.getCurrentTestQuestion();
      if (!currentQuestion || currentQuestion.id !== questionId) {
        console.warn('Question ID mismatch, using current question');
      }

      // Create response record
      const responseRecord = {
        questionId: questionId,
        response: response,
        responseTime: responseTime,
        timestamp: new Date().toISOString(),
        questionIndex: this.currentTest.currentQuestionIndex
      };

      // Store response
      this.currentTest.responses.push(responseRecord);
      this.currentTest.analytics.questionsAnswered += 1;
      this.currentTest.analytics.timeSpent += responseTime;

      console.log('✅ Test response submitted for question:', questionId);

      return {
        success: true,
        questionId: questionId,
        response: response,
        message: 'Response submitted successfully'
      };

    } catch (error) {
      console.error('❌ Error submitting test response:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  /**
   * Move to next test question
   */
  async moveToNextTestQuestion() {
    try {
      if (!this.currentTest) {
        throw new Error('No active test session');
      }

      this.currentTest.currentQuestionIndex += 1;

      if (this.currentTest.currentQuestionIndex >= this.currentTest.questions.length) {
        // Test completed
        return await this.completeTest();
      }

      const nextQuestion = this.getCurrentTestQuestion();

      return {
        success: true,
        completed: false,
        question: nextQuestion,
        questionIndex: this.currentTest.currentQuestionIndex,
        totalQuestions: this.currentTest.totalQuestions,
        progress: (this.currentTest.currentQuestionIndex / this.currentTest.totalQuestions) * 100
      };

    } catch (error) {
      console.error('❌ Error moving to next test question:', error);
      return {
        success: false,
        completed: true,
        error: error.message
      };
    }
  }

  /**
   * Get current test question
   */
  getCurrentTestQuestion() {
    try {
      if (!this.currentTest || !this.currentTest.questions) {
        return null;
      }

      const currentIndex = this.currentTest.currentQuestionIndex || 0;
      return this.currentTest.questions[currentIndex] || null;

    } catch (error) {
      console.error('Error getting current test question:', error);
      return null;
    }
  }

  /**
   * Complete test and generate results
   */
  async completeTest() {
    try {
      if (!this.currentTest) {
        throw new Error('No active test session');
      }

      this.currentTest.status = 'completed';
      this.currentTest.endTime = new Date().toISOString();

      // Calculate test duration
      const startTime = new Date(this.currentTest.startTime);
      const endTime = new Date(this.currentTest.endTime);
      const duration = Math.floor((endTime - startTime) / 1000); // seconds

      // Analyze responses
      const analysis = await this.analyzeTestResponses(this.currentTest);

      // Generate results
      const results = {
        testId: this.currentTest.testId,
        testType: this.currentTest.testType,
        candidateId: this.currentTest.candidateId,
        completedAt: this.currentTest.endTime,
        duration: duration,
        questionsAnswered: this.currentTest.responses.length,
        totalQuestions: this.currentTest.totalQuestions,
        completionRate: (this.currentTest.responses.length / this.currentTest.totalQuestions) * 100,
        analysis: analysis,
        recommendations: this.generateRecommendations(analysis, this.currentTest.testType),
        rawResponses: this.currentTest.responses
      };

      // Save results
      await this.saveTestResults(results);

      // Clear current test
      this.currentTest = null;

      console.log('✅ Psychometric test completed successfully');

      return {
        success: true,
        completed: true,
        results: results,
        message: 'Test completed successfully'
      };

    } catch (error) {
      console.error('❌ Error completing test:', error);
      return {
        success: false,
        completed: true,
        error: error.message
      };
    }
  }

  /**
   * Analyze test responses
   */
  async analyzeTestResponses(testSession) {
    try {
      const testType = testSession.testType;
      const responses = testSession.responses;

      let analysis = {};

      switch (testType) {
        case 'personality':
          analysis = this.analyzePersonalityResponses(responses, testSession.questions);
          break;
        case 'cognitive':
          analysis = this.analyzeCognitiveResponses(responses, testSession.questions);
          break;
        case 'behavioral':
          analysis = this.analyzeBehavioralResponses(responses, testSession.questions);
          break;
        case 'emotional_intelligence':
          analysis = this.analyzeEmotionalIntelligenceResponses(responses, testSession.questions);
          break;
        default:
          analysis = this.getDefaultAnalysis();
      }

      return analysis;

    } catch (error) {
      console.error('Error analyzing test responses:', error);
      return this.getDefaultAnalysis();
    }
  }

  /**
   * Analyze personality responses (Big Five)
   */
  analyzePersonalityResponses(responses, questions) {
    try {
      const traits = {
        openness: [],
        conscientiousness: [],
        extraversion: [],
        agreeableness: [],
        neuroticism: []
      };

      // Group responses by trait
      responses.forEach(response => {
        const question = questions.find(q => q.id === response.questionId);
        if (question && question.trait) {
          const score = question.reverse ? (6 - response.response) : response.response;
          traits[question.trait].push(score);
        }
      });

      // Calculate trait scores
      const traitScores = {};
      Object.keys(traits).forEach(trait => {
        if (traits[trait].length > 0) {
          const average = traits[trait].reduce((a, b) => a + b, 0) / traits[trait].length;
          traitScores[trait] = Math.round((average / 5) * 100); // Convert to percentage
        } else {
          traitScores[trait] = 50; // Default neutral score
        }
      });

      return {
        type: 'personality',
        model: 'big_five',
        scores: traitScores,
        profile: this.generatePersonalityProfile(traitScores),
        strengths: this.identifyPersonalityStrengths(traitScores),
        developmentAreas: this.identifyPersonalityDevelopmentAreas(traitScores)
      };

    } catch (error) {
      console.error('Error analyzing personality responses:', error);
      return this.getDefaultPersonalityAnalysis();
    }
  }

  /**
   * Analyze cognitive responses
   */
  analyzeCognitiveResponses(responses, questions) {
    try {
      const categories = {
        logical_reasoning: { correct: 0, total: 0 },
        numerical_reasoning: { correct: 0, total: 0 },
        spatial_reasoning: { correct: 0, total: 0 },
        verbal_reasoning: { correct: 0, total: 0 },
        working_memory: { correct: 0, total: 0 },
        processing_speed: { correct: 0, total: 0 }
      };

      // Analyze responses
      responses.forEach(response => {
        const question = questions.find(q => q.id === response.questionId);
        if (question && question.category) {
          const category = question.category;
          if (categories[category]) {
            categories[category].total += 1;
            if (question.correctAnswer !== undefined && response.response === question.correctAnswer) {
              categories[category].correct += 1;
            }
          }
        }
      });

      // Calculate scores
      const scores = {};
      Object.keys(categories).forEach(category => {
        if (categories[category].total > 0) {
          scores[category] = Math.round((categories[category].correct / categories[category].total) * 100);
        } else {
          scores[category] = 0;
        }
      });

      // Calculate overall IQ estimate
      const overallScore = Object.values(scores).reduce((a, b) => a + b, 0) / Object.values(scores).length;
      const iqEstimate = Math.round(85 + (overallScore / 100) * 30); // Scale to 85-115 range

      return {
        type: 'cognitive',
        overallScore: Math.round(overallScore),
        iqEstimate: iqEstimate,
        categoryScores: scores,
        strengths: this.identifyCognitiveStrengths(scores),
        developmentAreas: this.identifyCognitiveDevelopmentAreas(scores)
      };

    } catch (error) {
      console.error('Error analyzing cognitive responses:', error);
      return this.getDefaultCognitiveAnalysis();
    }
  }

  /**
   * Analyze behavioral responses
   */
  analyzeBehavioralResponses(responses, questions) {
    try {
      const traits = {
        leadership: [],
        teamwork: [],
        problem_solving: [],
        communication: [],
        adaptability: []
      };

      // Analyze responses (simplified scoring)
      responses.forEach(response => {
        const question = questions.find(q => q.id === response.questionId);
        if (question && question.category) {
          const score = this.scoreBehavioralResponse(question, response.response);
          if (traits[question.category]) {
            traits[question.category].push(score);
          }
        }
      });

      // Calculate trait scores
      const traitScores = {};
      Object.keys(traits).forEach(trait => {
        if (traits[trait].length > 0) {
          const average = traits[trait].reduce((a, b) => a + b, 0) / traits[trait].length;
          traitScores[trait] = Math.round(average);
        } else {
          traitScores[trait] = 50;
        }
      });

      return {
        type: 'behavioral',
        scores: traitScores,
        workStyle: this.determineWorkStyle(traitScores),
        strengths: this.identifyBehavioralStrengths(traitScores),
        developmentAreas: this.identifyBehavioralDevelopmentAreas(traitScores)
      };

    } catch (error) {
      console.error('Error analyzing behavioral responses:', error);
      return this.getDefaultBehavioralAnalysis();
    }
  }

  /**
   * Analyze emotional intelligence responses
   */
  analyzeEmotionalIntelligenceResponses(responses, questions) {
    try {
      const domains = {
        self_awareness: [],
        self_regulation: [],
        empathy: [],
        social_skills: [],
        motivation: []
      };

      // Group responses by domain
      responses.forEach(response => {
        const question = questions.find(q => q.id === response.questionId);
        if (question && question.category) {
          const score = response.response * 20; // Convert 1-5 scale to 0-100
          if (domains[question.category]) {
            domains[question.category].push(score);
          }
        }
      });

      // Calculate domain scores
      const domainScores = {};
      Object.keys(domains).forEach(domain => {
        if (domains[domain].length > 0) {
          const average = domains[domain].reduce((a, b) => a + b, 0) / domains[domain].length;
          domainScores[domain] = Math.round(average);
        } else {
          domainScores[domain] = 50;
        }
      });

      // Calculate overall EQ
      const overallEQ = Math.round(Object.values(domainScores).reduce((a, b) => a + b, 0) / Object.values(domainScores).length);

      return {
        type: 'emotional_intelligence',
        overallEQ: overallEQ,
        domainScores: domainScores,
        strengths: this.identifyEQStrengths(domainScores),
        developmentAreas: this.identifyEQDevelopmentAreas(domainScores)
      };

    } catch (error) {
      console.error('Error analyzing emotional intelligence responses:', error);
      return this.getDefaultEQAnalysis();
    }
  }

  // Helper methods for analysis
  scoreBehavioralResponse(question, responseIndex) {
    // Simplified scoring - in real implementation, this would be more sophisticated
    const scores = [25, 50, 75, 100]; // Scores for each option
    return scores[responseIndex] || 50;
  }

  generatePersonalityProfile(traitScores) {
    const profile = [];
    
    if (traitScores.openness > 70) profile.push('Creative and open to new experiences');
    if (traitScores.conscientiousness > 70) profile.push('Organized and detail-oriented');
    if (traitScores.extraversion > 70) profile.push('Outgoing and energetic');
    if (traitScores.agreeableness > 70) profile.push('Cooperative and trusting');
    if (traitScores.neuroticism < 30) profile.push('Emotionally stable and calm');
    
    return profile.length > 0 ? profile : ['Balanced personality profile'];
  }

  identifyPersonalityStrengths(traitScores) {
    const strengths = [];
    
    Object.entries(traitScores).forEach(([trait, score]) => {
      if (score > 70) {
        strengths.push(this.getTraitStrength(trait));
      }
    });
    
    return strengths.length > 0 ? strengths : ['Well-rounded personality'];
  }

  identifyPersonalityDevelopmentAreas(traitScores) {
    const areas = [];
    
    Object.entries(traitScores).forEach(([trait, score]) => {
      if (score < 40) {
        areas.push(this.getTraitDevelopmentArea(trait));
      }
    });
    
    return areas.length > 0 ? areas : ['Continue developing all areas'];
  }

  getTraitStrength(trait) {
    const strengths = {
      openness: 'Creative thinking and adaptability',
      conscientiousness: 'Organization and attention to detail',
      extraversion: 'Leadership and communication skills',
      agreeableness: 'Teamwork and collaboration',
      neuroticism: 'Emotional stability and stress management'
    };
    return strengths[trait] || 'Positive trait expression';
  }

  getTraitDevelopmentArea(trait) {
    const areas = {
      openness: 'Embrace new ideas and approaches',
      conscientiousness: 'Improve organization and planning',
      extraversion: 'Develop communication and networking skills',
      agreeableness: 'Build trust and cooperation skills',
      neuroticism: 'Develop stress management techniques'
    };
    return areas[trait] || 'Continue personal development';
  }

  identifyCognitiveStrengths(scores) {
    const strengths = [];
    
    Object.entries(scores).forEach(([category, score]) => {
      if (score > 70) {
        strengths.push(this.getCognitiveStrength(category));
      }
    });
    
    return strengths.length > 0 ? strengths : ['Solid cognitive abilities'];
  }

  identifyCognitiveDevelopmentAreas(scores) {
    const areas = [];
    
    Object.entries(scores).forEach(([category, score]) => {
      if (score < 50) {
        areas.push(this.getCognitiveDevelopmentArea(category));
      }
    });
    
    return areas.length > 0 ? areas : ['Continue cognitive skill development'];
  }

  getCognitiveStrength(category) {
    const strengths = {
      logical_reasoning: 'Strong logical and analytical thinking',
      numerical_reasoning: 'Excellent mathematical and quantitative skills',
      spatial_reasoning: 'Good spatial visualization abilities',
      verbal_reasoning: 'Strong language and communication skills',
      working_memory: 'Excellent memory and information processing',
      processing_speed: 'Fast and efficient cognitive processing'
    };
    return strengths[category] || 'Good cognitive ability';
  }

  getCognitiveDevelopmentArea(category) {
    const areas = {
      logical_reasoning: 'Practice logical reasoning and problem-solving',
      numerical_reasoning: 'Improve mathematical and quantitative skills',
      spatial_reasoning: 'Develop spatial visualization abilities',
      verbal_reasoning: 'Enhance language and communication skills',
      working_memory: 'Practice memory and concentration exercises',
      processing_speed: 'Work on speed and accuracy in tasks'
    };
    return areas[category] || 'Continue skill development';
  }

  identifyBehavioralStrengths(traitScores) {
    const strengths = [];
    
    Object.entries(traitScores).forEach(([trait, score]) => {
      if (score > 70) {
        strengths.push(this.getBehavioralStrength(trait));
      }
    });
    
    return strengths.length > 0 ? strengths : ['Good behavioral competencies'];
  }

  identifyBehavioralDevelopmentAreas(traitScores) {
    const areas = [];
    
    Object.entries(traitScores).forEach(([trait, score]) => {
      if (score < 50) {
        areas.push(this.getBehavioralDevelopmentArea(trait));
      }
    });
    
    return areas.length > 0 ? areas : ['Continue behavioral skill development'];
  }

  getBehavioralStrength(trait) {
    const strengths = {
      leadership: 'Strong leadership and direction-setting abilities',
      teamwork: 'Excellent collaboration and team skills',
      problem_solving: 'Effective problem-solving approach',
      communication: 'Clear and effective communication',
      adaptability: 'High adaptability and flexibility'
    };
    return strengths[trait] || 'Good behavioral competency';
  }

  getBehavioralDevelopmentArea(trait) {
    const areas = {
      leadership: 'Develop leadership and influence skills',
      teamwork: 'Improve collaboration and team dynamics',
      problem_solving: 'Enhance problem-solving methodology',
      communication: 'Strengthen communication skills',
      adaptability: 'Increase flexibility and adaptability'
    };
    return areas[trait] || 'Continue skill development';
  }

  determineWorkStyle(traitScores) {
    // Simplified work style determination
    if (traitScores.leadership > 70 && traitScores.communication > 70) {
      return 'Collaborative Leader';
    } else if (traitScores.problem_solving > 70 && traitScores.adaptability > 70) {
      return 'Innovative Problem Solver';
    } else if (traitScores.teamwork > 70) {
      return 'Team Player';
    } else {
      return 'Independent Contributor';
    }
  }

  identifyEQStrengths(domainScores) {
    const strengths = [];
    
    Object.entries(domainScores).forEach(([domain, score]) => {
      if (score > 70) {
        strengths.push(this.getEQStrength(domain));
      }
    });
    
    return strengths.length > 0 ? strengths : ['Good emotional intelligence'];
  }

  identifyEQDevelopmentAreas(domainScores) {
    const areas = [];
    
    Object.entries(domainScores).forEach(([domain, score]) => {
      if (score < 50) {
        areas.push(this.getEQDevelopmentArea(domain));
      }
    });
    
    return areas.length > 0 ? areas : ['Continue EQ development'];
  }

  getEQStrength(domain) {
    const strengths = {
      self_awareness: 'High self-awareness and emotional insight',
      self_regulation: 'Excellent emotional self-control',
      empathy: 'Strong empathy and understanding of others',
      social_skills: 'Excellent interpersonal and social skills',
      motivation: 'High intrinsic motivation and drive'
    };
    return strengths[domain] || 'Good emotional competency';
  }

  getEQDevelopmentArea(domain) {
    const areas = {
      self_awareness: 'Develop greater self-awareness',
      self_regulation: 'Improve emotional self-regulation',
      empathy: 'Enhance empathy and perspective-taking',
      social_skills: 'Strengthen social and interpersonal skills',
      motivation: 'Increase intrinsic motivation and goal-setting'
    };
    return areas[domain] || 'Continue EQ development';
  }

  /**
   * Generate recommendations based on analysis
   */
  generateRecommendations(analysis, testType) {
    try {
      const recommendations = [];
      
      switch (testType) {
        case 'personality':
          recommendations.push('Leverage your personality strengths in team collaboration');
          recommendations.push('Consider roles that align with your personality profile');
          break;
        case 'cognitive':
          if (analysis.overallScore > 70) {
            recommendations.push('Consider roles requiring strong analytical thinking');
          } else {
            recommendations.push('Practice cognitive exercises to improve reasoning skills');
          }
          break;
        case 'behavioral':
          recommendations.push('Focus on developing your behavioral competencies');
          recommendations.push('Seek opportunities to practice leadership and teamwork');
          break;
        case 'emotional_intelligence':
          if (analysis.overallEQ > 70) {
            recommendations.push('Consider roles requiring high emotional intelligence');
          } else {
            recommendations.push('Practice mindfulness and emotional awareness exercises');
          }
          break;
      }
      
      return recommendations;
      
    } catch (error) {
      console.error('Error generating recommendations:', error);
      return ['Continue developing your skills and competencies'];
    }
  }

  /**
   * Save test results
   */
  async saveTestResults(results) {
    try {
      const existingResults = await AsyncStorage.getItem('psychometric_test_results');
      const allResults = existingResults ? JSON.parse(existingResults) : [];
      
      allResults.push(results);
      
      await AsyncStorage.setItem('psychometric_test_results', JSON.stringify(allResults));
      
      console.log('✅ Test results saved');
      
    } catch (error) {
      console.error('❌ Error saving test results:', error);
    }
  }

  /**
   * Get fallback test when initialization fails
   */
  getFallbackTest(testType, candidateData) {
    try {
      const fallbackQuestions = this.getDefaultQuestions(testType);
      
      const testSession = {
        testId: `psychometric_${testType}_fallback_${Date.now()}`,
        testType: testType,
        candidateId: this.safeGet(candidateData, 'candidateId', 'unknown'),
        candidateData: candidateData || {},
        jobData: {},
        questions: fallbackQuestions,
        currentQuestionIndex: 0,
        responses: [],
        startTime: new Date().toISOString(),
        status: 'in_progress',
        timeLimit: 1200,
        totalQuestions: fallbackQuestions.length,
        analytics: {
          timeSpent: 0,
          questionsAnswered: 0,
          averageResponseTime: 0,
          confidenceLevel: 0
        }
      };
      
      this.currentTest = testSession;
      
      return {
        success: true,
        testId: testSession.testId,
        testType: testType,
        totalQuestions: fallbackQuestions.length,
        timeLimit: testSession.timeLimit,
        firstQuestion: fallbackQuestions[0],
        message: 'Psychometric test started with fallback questions',
        fallback: true
      };
      
    } catch (error) {
      console.error('Error creating fallback test:', error);
      return {
        success: false,
        error: 'Failed to initialize test',
        testType: testType
      };
    }
  }

  /**
   * Get default questions for test type
   */
  getDefaultQuestions(testType) {
    const defaultQuestions = {
      personality: [
        {
          id: 'personality_default_1',
          category: 'openness',
          question: 'I enjoy trying new things.',
          type: 'likert',
          scale: 5,
          options: ['Strongly Disagree', 'Disagree', 'Neutral', 'Agree', 'Strongly Agree'],
          trait: 'openness',
          reverse: false
        },
        {
          id: 'personality_default_2',
          category: 'conscientiousness',
          question: 'I am organized and methodical.',
          type: 'likert',
          scale: 5,
          options: ['Strongly Disagree', 'Disagree', 'Neutral', 'Agree', 'Strongly Agree'],
          trait: 'conscientiousness',
          reverse: false
        }
      ],
      cognitive: [
        {
          id: 'cognitive_default_1',
          category: 'logical_reasoning',
          question: 'If A > B and B > C, then A > C. This statement is:',
          type: 'multiple_choice',
          options: ['Always true', 'Sometimes true', 'Never true', 'Cannot determine'],
          correctAnswer: 0,
          timeLimit: 120
        }
      ],
      behavioral: [
        {
          id: 'behavioral_default_1',
          category: 'teamwork',
          question: 'In team projects, I prefer to:',
          type: 'multiple_choice',
          options: [
            'Take the lead and coordinate activities',
            'Contribute my expertise in specific areas',
            'Support others and help where needed',
            'Work independently on assigned tasks'
          ],
          traits: ['teamwork', 'leadership']
        }
      ],
      emotional_intelligence: [
        {
          id: 'emotional_default_1',
          category: 'self_awareness',
          question: 'I understand my own emotions well.',
          type: 'likert',
          scale: 5,
          options: ['Never', 'Rarely', 'Sometimes', 'Often', 'Always'],
          trait: 'self_awareness'
        }
      ]
    };
    
    return defaultQuestions[testType] || defaultQuestions.personality;
  }

  /**
   * Get default test bank
   */
  getDefaultTestBank() {
    return {
      personality: this.getDefaultQuestions('personality'),
      cognitive: this.getDefaultQuestions('cognitive'),
      behavioral: this.getDefaultQuestions('behavioral'),
      emotional_intelligence: this.getDefaultQuestions('emotional_intelligence')
    };
  }

  /**
   * Get default analysis
   */
  getDefaultAnalysis() {
    return {
      type: 'general',
      scores: { overall: 50 },
      strengths: ['Test completed successfully'],
      developmentAreas: ['Continue skill development'],
      recommendations: ['Practice and continue learning']
    };
  }

  getDefaultPersonalityAnalysis() {
    return {
      type: 'personality',
      model: 'big_five',
      scores: {
        openness: 50,
        conscientiousness: 50,
        extraversion: 50,
        agreeableness: 50,
        neuroticism: 50
      },
      profile: ['Balanced personality profile'],
      strengths: ['Well-rounded personality'],
      developmentAreas: ['Continue personal development']
    };
  }

  getDefaultCognitiveAnalysis() {
    return {
      type: 'cognitive',
      overallScore: 50,
      iqEstimate: 100,
      categoryScores: {
        logical_reasoning: 50,
        numerical_reasoning: 50,
        spatial_reasoning: 50,
        verbal_reasoning: 50
      },
      strengths: ['Solid cognitive abilities'],
      developmentAreas: ['Continue cognitive development']
    };
  }

  getDefaultBehavioralAnalysis() {
    return {
      type: 'behavioral',
      scores: {
        leadership: 50,
        teamwork: 50,
        problem_solving: 50,
        communication: 50,
        adaptability: 50
      },
      workStyle: 'Balanced Contributor',
      strengths: ['Good behavioral competencies'],
      developmentAreas: ['Continue behavioral development']
    };
  }

  getDefaultEQAnalysis() {
    return {
      type: 'emotional_intelligence',
      overallEQ: 50,
      domainScores: {
        self_awareness: 50,
        self_regulation: 50,
        empathy: 50,
        social_skills: 50,
        motivation: 50
      },
      strengths: ['Good emotional intelligence'],
      developmentAreas: ['Continue EQ development']
    };
  }

  /**
   * Utility method to shuffle array
   */
  shuffleArray(array) {
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  }

  /**
   * Get service status
   */
  getStatus() {
    return {
      initialized: this.isInitialized,
      activeTest: !!this.currentTest,
      testBank: this.testBank ? 'loaded' : 'not_loaded',
      analysisEngine: this.analysisEngine ? 'loaded' : 'not_loaded',
      supportedTests: ['personality', 'cognitive', 'behavioral', 'emotional_intelligence']
    };
  }
}

// Export singleton instance
const psychometricTestService = new PsychometricTestService();
export default psychometricTestService;
